import { negate } from 'lodash-es'
import React, { ReactElement } from 'react'

import { FormFormatter, isAnsweredInput } from '../../application/formBuilder'
import { isHiddenConfig, isInputConfig } from '../../application/formBuilder/config'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../infrastructure/internationalisation'
import { useAdjustments } from '../../state'
import { ChangeConfig, FormConfig, InputConfig } from '../../state/configuration'

type ExpandableSectionHeader = string | ReactElement

type ExpandableSectionResult = [ExpandableSectionHeader, InputConfig[]]

interface ExpandableSectionConfigProps<T> {
  sectionId: string
  form: FormConfig
  tKey: TKeyBuilder
  toData: (adjustment: ChangeConfig) => T
}

export const useExpandableSectionConfig = <T,>({
  sectionId,
  form,
  tKey,
  toData,
}: ExpandableSectionConfigProps<T>): ExpandableSectionResult => {
  const { t } = useWriskTranslation()

  const { getAdjustment } = useAdjustments()

  const headerTranslation = t(tKey(sectionId), { defaultValue: null })

  const [first, ...others] = form
    .filter(isInputConfig)
    .filter(negate(isHiddenConfig))
    .filter((input) => {
      const adjustment = getAdjustment(input.name)
      return isAnsweredInput(toData(adjustment))
    })

  const adjustment = getAdjustment(first.name)

  const header: ExpandableSectionHeader = headerTranslation ?? (
    <FormFormatter input={first} value={toData(adjustment)} t={t} />
  )

  return [header, headerTranslation ? [first, ...others] : others]
}
